import type { YAxisStackLabelsOptions } from 'highcharts';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import useTranslation from '@hooks/useTranslation';

import { selectCostSavingsIncludeUnverifiedSavings } from '@pages/CostSavingsDetails/store/useCostSavingsStore';

import { selectAppPersistDarkMode } from '@store/useAppStorePersist';

import style from '../components/TotalCostSaving/style.module.scss';
import { formatNumber } from '../helpers/formatNumber';
import type { ITotalCostSavingsPoint, ITotalCostSavingsYearChartStats } from '../model/totalCostSavings';

export const useGetTotalCostSavingOptions = (yearData?: ITotalCostSavingsYearChartStats | null) => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const darkMode = selectAppPersistDarkMode();

  const includeUnverifiedSavings = selectCostSavingsIncludeUnverifiedSavings();

  const yAxisStackLabelsOptions: YAxisStackLabelsOptions = {
    enabled: true,
    useHTML: true,
    verticalAlign: 'top',
    align: 'center',
    textAlign: 'center',
    style: {
      color: darkMode ? '#E0E2E6' : '#4A4A4A', // --legend-for-header-text
      fontWeight: '500',
      fontSize: '12px',
      fontFamily: 'Roboto',
      lineHeight: '12px',
    },
  };

  const baseOptions: Highcharts.Options = {
    title: {
      text: '',
    },
    tooltip: {
      useHTML: true,
      outside: true,
      formatter: function () {
        const { point, series } = this;
        const { name: seriesName } = series;
        const { name, custom } = point as ITotalCostSavingsPoint;
        return `${seriesName}<br/> ${t(name)}<br/><b>${custom.realValue}</b>`;
      },
    },
    credits: { enabled: false },
    legend: {
      symbolRadius: 0,
      align: 'center',
      useHTML: true,
      className: style.legend,
      reversed: true,
      itemStyle: {
        cursor: 'default',
      },
      itemHiddenStyle: {
        color: '#ccc',
      },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        states: {
          hover: {
            opacity: 1,
            brightness: 0,
          },
        },
        events: {
          click: function (event) {
            navigate(`/customer/${customerId}/executive-summary/cost-savings-details`, {
              state: {
                monthName: event.point.name,
                prevPage: pathname,
              },
            });
          },
        },
      },
    },
    xAxis: {
      type: 'category',
      labels: {
        rotation: 0,
      },
      className: style.xAxisLabels,
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        formatter: function () {
          return this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
      },
      className: style.yAxisLabels,
    },
    accessibility: { enabled: false },
  };

  const costSavingsOptions: Highcharts.Options = {
    ...baseOptions,
    yAxis: {
      ...baseOptions.yAxis,
      stackLabels: {
        ...yAxisStackLabelsOptions,
        formatter: function () {
          return this.total ? formatNumber(this.total) : '00.00';
        },
      },
    },
    series: [
      {
        type: 'column',
        name: `${t('Unverified')}`,
        data: structuredClone(yearData?.byMonthPotentialCostSavings || []),
        color: '#e4e4e4',
        visible: includeUnverifiedSavings,
      },
      {
        type: 'column',
        name: `${t('Verified')}`,
        data: structuredClone(yearData?.byMonthCostSavings || []),
        color: '#3c97aa',
      },
    ],
  };

  const downtimeAvoidedOptions: Highcharts.Options = {
    ...baseOptions,
    yAxis: {
      ...baseOptions.yAxis,
      stackLabels: {
        ...yAxisStackLabelsOptions,
        formatter: function () {
          return `${this.total.toFixed(2)}`;
        },
      },
    },
    series: [
      {
        type: 'column',
        name: `${t('Unverified')}`,
        data: structuredClone(yearData?.byMonthPotentialDowntimeAvoided || []),
        color: '#e4e4e4',
        visible: includeUnverifiedSavings,
      },
      {
        type: 'column',
        name: `${t('Verified')}`,
        data: structuredClone(yearData?.byMonthDowntimeAvoided || []),
        color: '#3c97aa',
      },
    ],
  };

  return { costSavingsOptions, downtimeAvoidedOptions };
};
